import { Injectable } from '@angular/core';
import { BaseService } from '../base';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar'; 
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../../models/base';
import { Vacaciones } from '../../models/personal/vacaciones.model';

const VACACIONES_ENDPOINT = '/vacaciones/';

@Injectable({
  providedIn: 'root',
})
export class VacacionesService extends BaseService {
  // #region Propiedades del servicio
  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private vacaciones: Vacaciones[] = [];

  vacacionesSolicitadas: Vacaciones[] = null;
  vs$: BehaviorSubject<Vacaciones[]> = new BehaviorSubject<Vacaciones[]>([]);
  vacacionesSolicitadas$: Observable<Vacaciones[]> = this.vs$.asObservable();

  vacacionesAprobadas: Vacaciones[] = null;
  va$: BehaviorSubject<Vacaciones[]> = new BehaviorSubject<Vacaciones[]>([]);
  vacacionesAprobadas$: Observable<Vacaciones[]> = this.va$.asObservable();
  // #endregion

  constructor(private http: HttpClient, protected snackBar: MatSnackBar) {
    super(snackBar);
  }

  // #region Métodos de consulta a la API

  /**
   * Método para eliminar una solicitud de vacaciones.
   * @param id Identificador de la solicitud de vacaciones
   */
  deleteVacaciones(id: number): Observable<boolean> {
    this.loading$.next(true);

    return this.http
      .delete<ApiResponse<boolean>>(environment.apiUrl + VACACIONES_ENDPOINT + id)
      .pipe(
        map((resultado: ApiResponse<boolean>) => {
          if (resultado && resultado.data) {
            this.vacaciones = this.vacaciones.filter((v) => v.id !== id);
            this.procesarVacaciones();

            this.snackBar.open('Vacaciones correctamente eliminadas', undefined, {
              duration: 3000,
            });

            return true;
          }

          this.procesarError(resultado);
          throw resultado;
        }),
        catchError((error) => {
          this.procesarError(error && error.error ? error.error : error);
          return throwError(error);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
  }

  /**
   * Método para obtener las vacaciones de los empleados asociados con un usuario.
   */
  getVacaciones(): void {
    this.loading$.next(true);

    this.http.get<ApiResponse<Vacaciones[]>>(environment.apiUrl + VACACIONES_ENDPOINT)
      .pipe(
        finalize(() => {
          this.loading$.next(false);
        })
      )
      .subscribe({
        next: (resultado: ApiResponse<Vacaciones[]>) => {
          if (resultado && resultado.data) {
            this.vacaciones = resultado.data;
            this.procesarVacaciones();
          } else if (resultado && resultado.error) {
            this.procesarError(resultado);
          }
        },
        error: (error) => {
          this.procesarError(error);
        },
      });
  }

  /**
   * Método para obtener las vacaciones de un empleado.
   * @param id Identificador del empleado
   */
  getVacacionesPorEmpleado(id: number): Observable<Vacaciones[]> {
    this.loading$.next(true);

    return this.http
      .get<ApiResponse<Vacaciones[]>>(environment.apiUrl + VACACIONES_ENDPOINT + 'empleado/' + id)
      .pipe(
        map((resultado: ApiResponse<Vacaciones[]>) => {
          if (resultado && resultado.data) {
            if (resultado.data.length > 0) {
              return resultado.data;
            }

            this.snackBar.open('No hay vacaciones para el empleado seleccionado', undefined, {
              duration: 3000,
            });
            return [];
          }

          this.procesarError(resultado);
          throw resultado;
        }),
        catchError((error) => {
          this.procesarError(error && error.error ? error.error : error);
          return throwError(error);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
  }

  /**
   * Método para obtener las vacaciones en un rango de fechas
   * @param inicio Fecha de inicio del rango
   * @param fin Fecha de fin del rango
   */
  getVacacionesPorFechas(inicio: string, fin: string): Observable<Vacaciones[]> {
    return this.http
      .get<ApiResponse<Vacaciones[]>>(environment.apiUrl + VACACIONES_ENDPOINT + 'rango/' + inicio + '/' + fin)
      .pipe(
        map((resultado: ApiResponse<Vacaciones[]>) => {
          if (resultado && resultado.data) {
            return resultado.data;
          }

          throw resultado;
        }),
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  newVacaciones(): Vacaciones {
    return {
      id: 0,
      cadenaFechaAprobacion: '',
      cadenaFechaRechazo: '',
      cadenaFechaSolicitud: '',
      cadenaFin: '',
      cadenaInicio: '',
      employees_Id: undefined,
      estado: 2,
      fechaAprobacion: null,
      fechaRechazo: null,
      fechaSolicitud: null,
      fin: null,
      inicio: null,
      motivo: '',
      motivoRechazo: '',
      users_id: 0,
      nombreEstado: '',
    };
  }

  /**
   * Método para guardar una solicitud de vacaciones.
   * @param vacaciones Solicitud de vacaciones a guardar
   */
  saveVacaciones(vacaciones: Vacaciones): Observable<Vacaciones> {
    this.loading$.next(true);

    const request$ =
      vacaciones.id > 0
        ? this.http.put<ApiResponse<Vacaciones>>(environment.apiUrl + VACACIONES_ENDPOINT, vacaciones)
        : this.http.post<ApiResponse<Vacaciones>>(environment.apiUrl + VACACIONES_ENDPOINT, vacaciones);

    return request$.pipe(
      tap((resultado: ApiResponse<Vacaciones>) => {
        if (resultado && resultado.data) {
          if (this.vacaciones.find((v) => v.id === resultado.data.id) == null) {
            this.vacaciones.push(resultado.data);
          } else {
            this.vacaciones = this.vacaciones.map((v) =>
              v.id === resultado.data.id ? resultado.data : v
            );
          }

          this.procesarVacaciones();

          this.snackBar.open('Vacaciones correctamente guardadas', undefined, {
            duration: 3000,
          });
        } else if (resultado && resultado.error) {
          this.procesarError(resultado);
          throw resultado;
        }
      }),
      map((resultado: ApiResponse<Vacaciones>) => resultado.data),
      catchError((error) => {
        this.procesarError(error && error.error ? error.error : error);
        return throwError(error);
      }),
      finalize(() => {
        this.loading$.next(false);
      })
    );
  }

  // #endregion

  // #region Métodos privados

  /**
   * Método para procesar las vacaciones obtenidas de la API.
   */
  private procesarVacaciones(): void {
    this.vacacionesSolicitadas = this.vacaciones.filter((v) => v.estado === 1);
    this.vs$.next(this.vacacionesSolicitadas);

    this.vacacionesAprobadas = this.vacaciones.filter((v) => v.estado === 2);
    this.va$.next(this.vacacionesAprobadas);
  }

  // #endregion
}